import { isNaN, isNumber, toNumber, isFinite, debounce } from 'lodash-es';
import Decimal from 'decimal.js';
import { ltpFeedService } from './equity/ltpFeedService';

function isEmptyOrUndefined(value) {
	const isUndefined = value === undefined;
	const isNull = value === null;
	const isEmptyObject =
		!isNull && typeof value === 'object' && Object.keys(value).length === 0;
	const isEmptyString =
		typeof value === 'string' && value.trim().length === 0;
	const isEmptyValue =
		isUndefined || isNull || isEmptyObject || isEmptyString;
	return isEmptyValue;
}

const getNumberInDecimalFormat = (number = '', decimalPlaces = 2) => {
	if (isNaN(number)) return '';
	if (isEmptyOrUndefined(number)) return number;
	const floatNumber = parseFloat(number);
	const decimalNumber = isNumber(floatNumber)
		? floatNumber?.toFixed(decimalPlaces)
		: number;
	return decimalNumber;
};

const isUndefinedOrNull = (value) => value === undefined || value === null;

const formatNumber = (
	number = '',
	{
		decimalPlaces = 2,
		addCurrencySymbol = false,
		addSign = false,
		formatAsPercentage = false,
		retainNegativeSign = true,
		defaultValue = '-',
	} = {},
	useDecimalLibrary = false,
) => {
	const num = toNumber(number);

	if (isEmptyOrUndefined(number) || !isValidNumber(number)) {
		return defaultValue;
	}

	const options = {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces,
	};
	const decimal = new Decimal(num).toDecimalPlaces(
		decimalPlaces,
		Decimal.ROUND_UP,
	);
	let truncatedNum = useDecimalLibrary
		? decimal
		: truncateNumber(num, decimalPlaces);
	if (formatAsPercentage) {
		truncatedNum /= 100;
		options.style = 'percent';
	}
	const formatter = new Intl.NumberFormat('en-IN', options);
	let formattedNumber = formatter.format(Math.abs(truncatedNum));
	if (addCurrencySymbol) {
		formattedNumber = `₹${formattedNumber}`;
	}

	if (addSign) {
		if (num > 0) {
			formattedNumber = `+${formattedNumber}`;
		} else if (num < 0) {
			formattedNumber = `-${formattedNumber}`;
		}
	} else if (retainNegativeSign) {
		if (num < 0) {
			formattedNumber = `-${formattedNumber}`;
		}
	}

	return formattedNumber;
};

function isValidNumber(value) {
	if (isEmptyOrUndefined(value)) {
		return false;
	}
	const numValue = Number(value);
	if (!Number.isNaN(numValue) && isFinite(numValue)) {
		return true;
	}
	return false;
}

const isDecimalNumber = (value) => {
	if (commonBaseService.isValidNumber(value)) {
		const decimalNumber = parseFloat(value);
		const remainder = decimalNumber % 1;
		const isDecimalNumber = remainder !== 0;
		return isDecimalNumber;
	}
	return false;
};

// truncates  decimal number without rounding off last digit
const truncateNumber = (number = '', decimalPlaces = 2) => {
	if (isEmptyOrUndefined(number) || isEmptyOrUndefined(decimalPlaces)) {
		return number;
	}

	const decimalNumber = Number(number);
	if (!isValidNumber(decimalNumber) || !isValidNumber(decimalPlaces)) {
		return number;
	}
	const decimalMultiplier = Math.pow(10, decimalPlaces);
	let truncatedNum =
		parseInt(decimalMultiplier * decimalNumber) / decimalMultiplier;
	truncatedNum = Number(truncatedNum.toFixed(decimalPlaces));
	return truncatedNum;
};

const avoidMultiplePress = (onPress, delayTime = 500) => {
	return debounce(onPress, delayTime, {
		leading: true,
		trailing: false,
	});
};
function formatAmountStringToNumber(str = '') {
	if (typeof str !== 'string') {
		return str;
	}
	const numStr = str?.replace(/,/g, '');
	if (isNaN(numStr)) {
		return str;
	}

	return numStr;
}
export const maskAccountNumber = (accountNumber) => {
	return `XXXX${accountNumber?.substr(-4)}`;
};

function formatNumberWithLetterAtEndRepresentation(
	number = '',
	addSign = false,
	isCurrencySymbol = false,
) {
	let num = parseFloat(number);
	if (isEmptyOrUndefined(number) || !isValidNumber(number)) {
		return '-';
	}

	const symbols = ['', 'K', 'L', 'Cr'];
	num = Math.abs(num);
	let index = 0;
	if (num >= 1000) {
		while (index < symbols.length - 1) {
			let n = num / (1000 * Math.pow(10, 2 * index)); //23.8
			index++;
			if (n < 100) {
				num = n;
				break;
			}
		}
	}
	const formattedNumber = formatNumber(num.toString(), {
		decimalPlaces: 2,
		addCurrencySymbol: isCurrencySymbol,
		addSign: addSign,
		formatAsPercentage: false,
		retainNegativeSign: false,
	});
	return `${formattedNumber}${symbols[index]}`;
}
const getFirstCharacter = (symbol) => {
	return symbol?.charAt(0).toLowerCase();
};

const openInterestRepresentation = (openInterest) => {
	const sanitizeOpenInterest = ltpFeedService.removeNegativeOrPositiveSign(
		openInterest.toString(),
	);
	const formattedOpenInterest =
		ltpFeedService.formatVolume(sanitizeOpenInterest);
	return formattedOpenInterest;
};

const ceilToNearestMultiple = (number, multiple = 10) => {
	if (!isValidNumber(number) || !isValidNumber(multiple) || multiple === 0) {
		return number;
	}
	const _number = Number(number);
	const _multiple = Number(multiple);
	return Math.ceil(_number / _multiple) * _multiple;
};

const commonBaseService = {
	getNumberInDecimalFormat,
	formatNumber,
	isValidNumber,
	isEmptyOrUndefined,
	isUndefinedOrNull,
	truncateNumber,
	isDecimalNumber,
	avoidMultiplePress,
	formatAmountStringToNumber,
	maskAccountNumber,
	formatNumberWithLetterAtEndRepresentation,
	getFirstCharacter,
	ceilToNearestMultiple,
	openInterestRepresentation,
};

export default commonBaseService;
